import { ErrorResponse, fetchAPI } from '@quickbit/backoffice-core-frontend';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export interface CustomerFile {
  uuid: string;
  originalFilename: string;
  contentType: string;
  createdBy: string;
  createdAt: string;
  fileUrl: string;
}

export interface CustomerFilesResponse {
  files: CustomerFile[];
}

const queryKeys = {
  all: () => ['fcu', 'customer-files'] as const,
  list: (customerId?: string) =>
    [...queryKeys.all(), 'list', customerId] as const,
};

export const useGetCustomerFiles = (customerId: string) => {
  return useQuery<CustomerFilesResponse, ErrorResponse>({
    queryKey: queryKeys.list(customerId),
    queryFn: async () => {
      const params = new URLSearchParams({ customer_id: customerId });
      return await fetchAPI<CustomerFilesResponse>(
        `v1/fcu/customer-files?${params.toString()}`
      );
    },
    enabled: !!customerId,
    placeholderData: keepPreviousData,
  });
};

export const useUploadCustomerFile = (customerId: string) => {
  const queryClient = useQueryClient();
  return useMutation<{ uuid: string }, ErrorResponse, File>({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('customer_id', customerId);

      const response = await fetchAPI<{ uuid: string }>(
        'v1/fcu/customer-files',
        {
          method: 'POST',
          body: formData,
        }
      );
      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.list(customerId),
      });
    },
  });
};

export const useDeleteCustomerFile = (customerId: string) => {
  const queryClient = useQueryClient();
  return useMutation<unknown, ErrorResponse, string>({
    mutationFn: async (fileUuid: string) => {
      await fetchAPI(`v1/fcu/customer-files/${fileUuid}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.list(customerId),
      });
    },
  });
};
